<script setup lang="ts">
import { computed, ref, watch, type Ref } from 'vue';
import type { QSelect } from 'quasar';
import {
  mdiAccountMultipleCheck,
  mdiBookOpenVariant,
  mdiCloseCircleOutline,
  mdiClose,
  mdiSelectAll,
  mdiViewList,
} from '@quasar/extras/mdi-v7';

type AssignmentItemTab = 'courses' | 'sections';

type OptionItem = {
  value: string;
  label: string;
};

const props = defineProps<{
  modelValue: boolean;
  studentOptions: OptionItem[];
  courseOptions: OptionItem[];
  sectionOptions: OptionItem[];
  selectedStudentIds: string[];
  selectedCourseIds: string[];
  selectedSectionIds: string[];
  initialTab?: AssignmentItemTab;
  isApplying?: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (
    e: 'apply',
    payload: {
      studentIds: string[];
      courseIds: string[];
      sectionIds: string[];
    },
  ): void;
}>();

type SearchKey = 'students' | 'courses' | 'sections';

const activeTab = ref<AssignmentItemTab>('courses');
const studentIds = ref<string[]>([]);
const courseIds = ref<string[]>([]);
const sectionIds = ref<string[]>([]);

// What the teacher typed into each select (`use-input` filters nothing on its own).
const searchText = ref<Record<SearchKey, string>>({
  students: '',
  courses: '',
  sections: '',
});

function matchOptions(options: OptionItem[], needle: string) {
  const query = needle.trim().toLocaleLowerCase();
  if (!query) return options;
  return options.filter((item) =>
    item.label.toLocaleLowerCase().includes(query),
  );
}

const filteredStudentOptions = computed(() =>
  matchOptions(props.studentOptions, searchText.value.students),
);
const filteredCourseOptions = computed(() =>
  matchOptions(props.courseOptions, searchText.value.courses),
);
const filteredSectionOptions = computed(() =>
  matchOptions(props.sectionOptions, searchText.value.sections),
);

const selectRefs: Record<SearchKey, Ref<QSelect | null>> = {
  students: ref(null),
  courses: ref(null),
  sections: ref(null),
};
const studentSelect = selectRefs.students;
const courseSelect = selectRefs.courses;
const sectionSelect = selectRefs.sections;

// After a pick, clear what was typed so the next search starts empty (Quasar
// keeps the text in a multiple select), and show the full list again.
function onPicked(key: SearchKey) {
  selectRefs[key].value?.updateInputValue('', true);
  searchText.value[key] = '';
}

function onFilter(
  key: SearchKey,
  needle: string,
  update: (fn: () => void) => void,
) {
  update(() => {
    searchText.value[key] = needle;
  });
}

watch(
  () => props.modelValue,
  (isOpen) => {
    if (!isOpen) return;
    activeTab.value = props.initialTab || 'courses';
    searchText.value = { students: '', courses: '', sections: '' };
    // Preselection comes from who is already assigned, which can include
    // students no longer offered (dropped etc.) — keep only offered ones.
    const offered = new Set(props.studentOptions.map((item) => item.value));
    studentIds.value = (props.selectedStudentIds || []).filter((id) =>
      offered.has(id),
    );
    courseIds.value = [...(props.selectedCourseIds || [])];
    sectionIds.value = [...(props.selectedSectionIds || [])];
  },
  { immediate: true },
);

const itemIds = computed(() =>
  activeTab.value === 'courses' ? courseIds.value : sectionIds.value,
);

const itemOptions = computed(() =>
  activeTab.value === 'courses' ? props.courseOptions : props.sectionOptions,
);

const activeItemLabel = computed(() =>
  activeTab.value === 'courses' ? 'COURSES' : 'SECTIONS',
);

const canApply = computed(
  () =>
    studentIds.value.length > 0 &&
    (courseIds.value.length > 0 || sectionIds.value.length > 0) &&
    !props.isApplying,
);

function closeDialog() {
  emit('update:modelValue', false);
}

function onApply() {
  if (!canApply.value) return;
  emit('apply', {
    studentIds: [...studentIds.value],
    courseIds: [...courseIds.value],
    sectionIds: [...sectionIds.value],
  });
}

function selectAllStudents() {
  studentIds.value = props.studentOptions.map((item) => item.value);
}

function clearAllStudents() {
  studentIds.value = [];
}

function selectAllItems() {
  if (activeTab.value === 'courses') {
    courseIds.value = props.courseOptions.map((item) => item.value);
    return;
  }
  sectionIds.value = props.sectionOptions.map((item) => item.value);
}

function clearAllItems() {
  if (activeTab.value === 'courses') {
    courseIds.value = [];
    return;
  }
  sectionIds.value = [];
}
</script>

<template>
  <q-dialog
    :model-value="modelValue"
    persistent
    @update:model-value="emit('update:modelValue', $event)"
  >
    <q-card style="width: 840px; max-width: 96vw">
      <q-card-section class="row items-center">
        <div>
          <div class="text-subtitle1">{{ $t('ASSIGNMENTS') }}</div>
          <div class="text-caption text-muted">
            {{ $t('SELECT_AND_ASSIGN') }}
          </div>
        </div>
        <q-space />
        <q-btn
          dense
          flat
          round
          :icon="mdiClose"
          @click="closeDialog"
        />
      </q-card-section>

      <q-separator />

      <q-card-section class="q-gutter-md">
        <div class="text-caption text-muted text-weight-medium">
          1. {{ $t('STUDENTS') }}
        </div>
        <q-select
          ref="studentSelect"
          v-model="studentIds"
          multiple
          use-input
          use-chips
          emit-value
          map-options
          outlined
          dense
          :options="filteredStudentOptions"
          :label="$t('STUDENTS')"
          options-dense
          behavior="menu"
          input-debounce="0"
          @filter="(needle, update) => onFilter('students', needle, update)"
          @add="onPicked('students')"
        />
        <div class="row items-center q-col-gutter-sm">
          <div class="col-auto">
            <q-btn
              dense
              flat
              color="primary"
              :icon="mdiSelectAll"
              :label="$t('SELECT_ALL')"
              @click="selectAllStudents"
            />
          </div>
          <div class="col-auto">
            <q-btn
              dense
              flat
              color="primary"
              :icon="mdiCloseCircleOutline"
              :label="$t('CLEAR_ALL')"
              @click="clearAllStudents"
            />
          </div>
          <div class="col text-right text-caption text-muted">
            <q-icon
              :name="mdiAccountMultipleCheck"
              size="16px"
              class="q-mr-xs"
            />
            {{ `${studentIds.length}/${studentOptions.length}` }}
          </div>
        </div>

        <div class="text-caption text-muted text-weight-medium">
          2. {{ $t(activeItemLabel) }}
        </div>
        <div class="row items-center q-col-gutter-sm">
          <div class="col-auto">
            <q-btn-toggle
              v-model="activeTab"
              unelevated
              toggle-color="primary"
              color="grey-4"
              text-color="dark"
              :options="[
                {
                  label: $t('COURSES'),
                  value: 'courses',
                  icon: mdiBookOpenVariant,
                },
                { label: $t('SECTIONS'), value: 'sections', icon: mdiViewList },
              ]"
            />
          </div>
          <div class="col text-caption text-muted text-right">
            {{ itemOptions.length }}
          </div>
        </div>

        <q-select
          v-if="activeTab === 'courses'"
          ref="courseSelect"
          v-model="courseIds"
          multiple
          use-input
          use-chips
          emit-value
          map-options
          outlined
          dense
          :options="filteredCourseOptions"
          :label="$t('COURSES')"
          options-dense
          behavior="menu"
          input-debounce="0"
          @filter="(needle, update) => onFilter('courses', needle, update)"
          @add="onPicked('courses')"
        />

        <q-select
          v-else
          ref="sectionSelect"
          v-model="sectionIds"
          multiple
          use-input
          use-chips
          emit-value
          map-options
          outlined
          dense
          :options="filteredSectionOptions"
          :label="$t('SECTIONS')"
          options-dense
          behavior="menu"
          input-debounce="0"
          @filter="(needle, update) => onFilter('sections', needle, update)"
          @add="onPicked('sections')"
        />

        <div class="row items-center q-col-gutter-sm">
          <div class="col-auto">
            <q-btn
              dense
              flat
              color="primary"
              :icon="mdiSelectAll"
              :label="$t('SELECT_ALL')"
              @click="selectAllItems"
            />
          </div>
          <div class="col-auto">
            <q-btn
              dense
              flat
              color="primary"
              :icon="mdiCloseCircleOutline"
              :label="$t('CLEAR_ALL')"
              @click="clearAllItems"
            />
          </div>
          <div class="col text-right text-caption text-muted">
            <q-icon
              :name="activeTab === 'courses' ? mdiBookOpenVariant : mdiViewList"
              size="16px"
              class="q-mr-xs"
            />
            {{ `${itemIds.length}/${itemOptions.length}` }}
          </div>
        </div>

        <q-banner class="bg-grey-2 text-grey-8">
          {{
            `${$t('STUDENTS')}: ${studentIds.length} | ${$t('COURSES')}: ${courseIds.length} | ${$t('SECTIONS')}: ${sectionIds.length}`
          }}
        </q-banner>
      </q-card-section>

      <q-card-actions align="right">
        <q-btn
          color="primary"
          unelevated
          :label="$t('APPLY')"
          :disable="!canApply"
          :loading="isApplying"
          @click="onApply"
        />
        <q-btn
          flat
          :label="$t('CLOSE')"
          :disable="isApplying"
          @click="closeDialog"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>
